// ui/DropdownMenuPortal.js
import React, {
  useRef,
  useLayoutEffect,
  useEffect,
  useCallback,
  useState,
} from "react";
import { createPortal } from "react-dom";
import useClickOutside from "../hooks/useClickOutside";
import { firstEl } from "../utils/firstEl";

/* Portal-based anchored dropdown that clamps inside a boundary element (the modal card) */
export default function DropdownMenuPortal({
  anchorRef,
  boundaryRef, // the modal card (to keep menu within)
  onClose,
  items,
  side = "top",
  align = "left",
  scrollRefs = [],
  widthClass = "w-48",
  maxHeight, // optional: cap menu height (px) and scroll inside; disables flip-up for side="bottom"
}) {
  const menuRef = useRef(null);
  const itemRefs = useRef([]);
  
  // Keyboard navigation state (-1 means no item focused)
  const [focusIndex, setFocusIndex] = useState(-1);
  
  useClickOutside([menuRef, anchorRef], onClose);

  // Focus management
  const focusItem = useCallback((idx) => {
    const el = itemRefs.current[idx];
    if (el) el.focus();
  }, []);

  // Focus menu container on mount to enable keyboard navigation
  useEffect(() => {
    const menu = menuRef.current;
    if (menu) {
      // Small delay to ensure DOM is ready
      const timeoutId = setTimeout(() => {
        menu.focus();
      }, 0);
      return () => clearTimeout(timeoutId);
    }
  }, []);

  // Keyboard navigation handler
  const onKeyDown = useCallback(
    (e) => {
      const max = items.length - 1;

      if (e.key === "ArrowDown") {
        e.preventDefault();
        // If nothing focused, start at first item
        const next = focusIndex === -1 ? 0 : focusIndex >= max ? 0 : focusIndex + 1;
        setFocusIndex(next);
        focusItem(next);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        // If nothing focused, start at last item
        const prev = focusIndex === -1 ? max : focusIndex <= 0 ? max : focusIndex - 1;
        setFocusIndex(prev);
        focusItem(prev);
      } else if (e.key === "Home") {
        e.preventDefault();
        setFocusIndex(0);
        focusItem(0);
      } else if (e.key === "End") {
        e.preventDefault();
        setFocusIndex(max);
        focusItem(max);
      } else if (e.key === "Escape") {
        e.preventDefault();
        onClose?.();
      } else if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        // Only trigger if an item is focused
        if (focusIndex >= 0) {
          const item = items[focusIndex];
          if (item) {
            item.onSelect?.();
            onClose?.();
          }
        }
      }
    },
    [focusIndex, focusItem, items, onClose]
  );

  // compute & clamp position
  const compute = useCallback(() => {
    const anchor = anchorRef?.current;
    const menu = menuRef?.current;
    const boundary = boundaryRef?.current;

    if (!anchor || !menu) return { top: 0, left: 0, ready: false };

    /**
     * Get dimensions of each ref
     */
    const a = anchor.getBoundingClientRect();
    const m = menu.getBoundingClientRect();
    const pad = 8;

    // Capped-height mode (opt-in via the `maxHeight` prop, side="bottom" only):
    // anchor the menu strictly below the trigger, never flip up, and cap its
    // height to whichever is smaller — the requested maxHeight or the room
    // remaining to the boundary's bottom. The menu scrolls internally past that.
    if (maxHeight != null && side === "bottom") {
      const top = a.bottom + pad;
      let left = align === "right" ? a.right - m.width : a.left;

      const containerLeft = boundary
        ? boundary.getBoundingClientRect().left
        : 0;
      const containerRight = boundary
        ? boundary.getBoundingClientRect().right
        : window.innerWidth;
      const containerBottom = boundary
        ? boundary.getBoundingClientRect().bottom
        : window.innerHeight;

      const minLeft = containerLeft + pad;
      const maxLeft = containerRight - pad - m.width;
      left = Math.min(Math.max(left, minLeft), Math.max(minLeft, maxLeft));

      const availableBelow = containerBottom - top - pad;
      const effectiveMaxHeight = Math.max(
        80,
        Math.min(maxHeight, availableBelow),
      );

      return { top, left, effectiveMaxHeight, ready: true };
    }

    let top = side === "top" ? a.top - m.height - pad : a.bottom + pad;
    let left = align === "right" ? a.right - m.width : a.left;

    // Apply boundary clamping if boundary exists
    if (boundary) {
      const b = boundary.getBoundingClientRect();
      const minTop = b.top + pad;
      const maxTop = b.bottom - pad - m.height;
      const minLeft = b.left + pad;
      const maxLeft = b.right - pad - m.width;

      top = Math.min(Math.max(top, minTop), Math.max(minTop, maxTop));
      left = Math.min(Math.max(left, minLeft), Math.max(minLeft, maxLeft));
    } else {
      // No boundary - clamp to viewport instead
      const minTop = pad;
      const maxTop = window.innerHeight - pad - m.height;
      const minLeft = pad;
      const maxLeft = window.innerWidth - pad - m.width;

      top = Math.min(Math.max(top, minTop), Math.max(minTop, maxTop));
      left = Math.min(Math.max(left, minLeft), Math.max(minLeft, maxLeft));
    }

    return { top, left, ready: true };
  }, [anchorRef, boundaryRef, side, align, maxHeight]);

  const [pos, setPos] = useState({ top: 0, left: 0, ready: false });
  const update = useCallback(() => setPos(compute()), [compute]);

  /**
   * useLayoutEffect runs right after React has committed the DOM, but before the browser has painted the page
   * useLayoutEffect runs on first mount or when its dependencies change.
   * This hook is perfect when you want to read the layout and size before the viewer sees it
   * In this case, we want to render a menu which depends on the current layout.
   * With useLayoutEffect, we are able to get the anchor clamping position before the user sees the page
   * That way, we can render the menu atomically, at the correct place/position without there being a visual jump
   */
  useLayoutEffect(() => {
    /**
     * Once React has committed the DOM but the browser is not painted yet,
     * We will calculate the position of where the menu will be clamped.
     */
    update();
    /**
     * Because once the DOM has been committed, the layout and size of elements can change
     * This is because of fonts load, icons render, so the layout might not be accurate,
     * So we schedule another calculation for when they have rendered, but before the browser is painted.
     */
    const rId = requestAnimationFrame(update);
    /**
     * Cleanup before unmounting. Clean up the RAF (requestAnimationFrame)
     */
    return () => cancelAnimationFrame(rId);
  }, [update, items?.length]);

  /**
   * This useEffect will only run on mount, and when pos.ready, boundaryRef or scrollRefs, update change.
   * pos.ready only changes after useLayoutEffect has run and calculated the positions
   */
  useEffect(() => {
    if (!pos.ready) return;
    const boundary = firstEl([boundaryRef]);
    const extra1 = firstEl(scrollRefs);

    const onScroll = () => update();
    const onResize = () => update();

    /**
     * If there is any scroll or resize event,
     * update is called to re-calculate the positions
     */
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onScroll, true);
    if (boundary)
      boundary.addEventListener("scroll", onScroll, { passive: true });
    if (extra1) extra1.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onScroll, true);
      if (boundary) boundary.removeEventListener("scroll", onScroll);
      if (extra1) extra1.removeEventListener("scroll", onScroll);
    };
  }, [pos.ready, boundaryRef, scrollRefs, update]);

  // render to body as fixed so it never gets clipped by overflow
  /**
   * CreatePortal renders the children in a different DOM than the normal parent's DOM
   * Event handling still works the same.
   * React bridges the two components using synthetic events
   * We use createPortal in this case to avoid clipping.
   * Parents' overflow rules will not apply to this menu
   * React membership stays the same (props, state, handlers, etc...)
   * DOM placement differs
   */
  const capped = pos.effectiveMaxHeight != null;

  return createPortal(
    <div
      ref={menuRef}
      className={`fixed z-[100] ${widthClass} max-w-[calc(100vw-1rem)] rounded-xl bg-surface-elevated shadow-xl ring-1 ring-black/10 dropdown-anim ${capped ? "overflow-y-auto pretty-scroll" : ""}`}
      role="menu"
      aria-orientation="vertical"
      tabIndex={0}
      onKeyDown={onKeyDown}
      style={{
        top: pos.top,
        left: pos.left,
        ...(capped && { maxHeight: pos.effectiveMaxHeight }),
        outline: 'none', // Remove focus ring on container (focus shown on items instead)
        transformOrigin:
          side === "top"
            ? align === "right"
              ? "bottom right"
              : "bottom left"
            : align === "right"
            ? "top right"
            : "top left",
      }}
    >
      <ul className="py-1" role="none">
        {items.map((it, idx) => (
          <li key={idx} role="none">
            <button
              ref={(el) => (itemRefs.current[idx] = el)}
              className="w-full px-3 py-2 text-left text-sm text-text-primary hover:bg-surface focus:bg-surface-sunken focus:outline-none hover:rounded-md focus:rounded-md flex items-center gap-2"
              tabIndex={idx === focusIndex ? 0 : -1}
              onFocus={() => setFocusIndex(idx)}
              onClick={() => {
                it.onSelect?.();
                onClose?.();
              }}
              role="menuitem"
            >
              {it.icon}
              <span>{it.label}</span>
            </button>
          </li>
        ))}
      </ul>
    </div>,
    document.body
  );
}
